import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { authFetch } from '@verantos-dev/core-utils';
import type { ListResponse } from '@verantos-dev/types-common';

import { analyticalDatasetKeys } from './queryKeys';
import type { AnalyticalDatasetResultRow } from './types';

interface UseAnalyticalDatasetResultsQueryArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
  /** Serialized grid params (`page=…&pageSize=…&sort=…&filter=…&logicOperator=…`)
   * from the shared DataGrid. */
  searchParams?: string;
  enabled: boolean;
}

/**
 * One page of per-patient results for the profile's latest PROCESSED run,
 * with server-driven column definitions (same fields as the CSV export).
 */
export const useAnalyticalDatasetResultsQuery = ({
  projectId,
  analyticalDatasetProfileId,
  searchParams,
  enabled,
}: UseAnalyticalDatasetResultsQueryArgs) => {
  // The shared DataGrid serializes its grid state WITH a leading '?'
  // (useDataGridSearchParams). Re-parse to a canonical bare string so the URL
  // can't end up as `results??page=…` — the double '?' makes the backend read
  // the first param's key as '?page' and silently serve page 1 forever.
  const normalizedSearchParams = searchParams ? new URLSearchParams(searchParams).toString() : '';

  return useQuery({
    queryKey: analyticalDatasetKeys.resultsPage(analyticalDatasetProfileId, normalizedSearchParams),
    queryFn: () =>
      authFetch<ListResponse<AnalyticalDatasetResultRow>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}` +
          `/results${normalizedSearchParams ? `?${normalizedSearchParams}` : ''}`
      ),
    enabled: enabled && projectId.length > 0 && analyticalDatasetProfileId.length > 0,
    retry: false,
    // Keep the previous page's rows while the next page loads so the grid
    // doesn't collapse to the loading skeleton on every pagination click.
    placeholderData: keepPreviousData,
    // staleTime 0: the cache key is (profileId, page params), NOT execution-
    // scoped, so a cached page can outlive the run that produced it — e.g. leave
    // the tab while a reprocess runs and return after it finishes, and the
    // mounted-only invalidation effect never sees the PROCESSING→PROCESSED
    // transition. Refetching on every mount guarantees the grid reflects the
    // latest execution; the in-place invalidation still covers staying mounted.
    staleTime: 0,
  });
};
